import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import StickyHead from "../StickyHead";

/* 브라우저 기준 오늘 (toISOString 은 UTC 라 한국 오전 9시 전엔 어제가 된다) */
function localToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];
function withWeekday(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return `${ymd} (${WEEKDAYS[new Date(y, m - 1, d).getDay()]})`;
}

const emptyForm = () => ({ title: "", date: localToday(), time: "", project_id: "" });

export default function CalendarPage() {
  const [rows, setRows] = useState(null);
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState(null); // { ready, calendar_id }
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function load() {
    setError("");
    try {
      setRows(await api.get("/schedules"));
    } catch (err) { setError(err.message); }
  }
  useEffect(() => {
    load();
    api.get("/projects").then(setProjects).catch(() => setProjects([]));
    api.get("/schedules/status").then(setStatus).catch(() => setStatus(null));
  }, []);

  function startEdit(s) {
    setEditingId(s.id);
    setForm({ title: s.title, date: s.date, time: s.time || "", project_id: s.project_id ? String(s.project_id) : "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function cancelEdit() {
    setEditingId(null);
    setForm(emptyForm());
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError("할일을 입력하세요."); return; }
    if (!form.date) { setError("날짜를 선택하세요."); return; }
    setBusy(true); setError("");
    const body = {
      title: form.title.trim(),
      date: form.date,
      time: form.time || null,
      project_id: form.project_id ? Number(form.project_id) : null,
    };
    try {
      const saved = editingId
        ? await api.post(`/schedules/${editingId}`, body)
        : await api.post("/schedules", body);
      if (saved.gcal_error) setError(`일정은 저장했지만 캘린더에 올리지 못했습니다 — ${saved.gcal_error}`);
      cancelEdit();
      load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  async function resync(s) {
    setError("");
    try {
      const saved = await api.post(`/schedules/${s.id}/sync`);
      if (saved.gcal_error) setError(saved.gcal_error);
      load();
    } catch (err) { setError(err.message); }
  }

  async function remove(s) {
    if (!window.confirm(`"${s.title}" 일정을 삭제할까요? 구글 캘린더에서도 지워집니다.`)) return;
    setError("");
    try {
      await api.del(`/schedules/${s.id}`);
      if (editingId === s.id) cancelEdit();
      load();
    } catch (err) { setError(err.message); }
  }

  const today = localToday();
  const upcoming = (rows || []).filter((s) => s.date >= today);
  const past = (rows || []).filter((s) => s.date < today).reverse();

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>
        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">일정관리</h1>
            <p className="sstoa-sub">
              입력한 일정은 구글 캘린더{status ? ` (${status.calendar_id})` : ""}에 자동으로 추가됩니다.
            </p>
          </div>
        </div>
      </StickyHead>

      {status && !status.ready && (
        <div className="sstoa-notice">
          메일(SMTP) 설정이 없어 캘린더로 보낼 수 없습니다. 일정은 저장되지만 캘린더에는 올라가지 않습니다.
          설정 후 각 일정의 "다시 보내기"로 올릴 수 있습니다.
        </div>
      )}

      <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }} onSubmit={submit}>
        <div className="sstoa-field" style={{ flex: 2, minWidth: 200 }}>
          <label>할일</label>
          <input className="sstoa-input" value={form.title} onChange={upd("title")} disabled={busy} placeholder="예: 부품 발주 확인" />
        </div>
        <div className="sstoa-field" style={{ minWidth: 140 }}>
          <label>날짜</label>
          <input className="sstoa-input" type="date" value={form.date} onChange={upd("date")} disabled={busy} />
        </div>
        <div className="sstoa-field" style={{ minWidth: 120 }}>
          <label>시간 (비우면 종일)</label>
          <input className="sstoa-input" type="time" value={form.time} onChange={upd("time")} disabled={busy} />
        </div>
        <div className="sstoa-field" style={{ flex: 1, minWidth: 180 }}>
          <label>관련 프로젝트</label>
          <select className="sstoa-select" value={form.project_id} onChange={upd("project_id")} disabled={busy}>
            <option value="">— 선택 안 함 —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="submit" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" disabled={busy}>
            {busy ? "저장 중…" : editingId ? "수정 저장" : "일정 추가"}
          </button>
          {editingId && <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={cancelEdit} disabled={busy}>취소</button>}
        </div>
      </form>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : upcoming.length === 0 ? (
          <div className="sstoa-empty">다가오는 일정이 없습니다.</div>
        ) : (
          <ScheduleTable rows={upcoming} today={today} editingId={editingId} onEdit={startEdit} onRemove={remove} onResync={resync} />
        )}
      </div>

      {past.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => setShowPast((v) => !v)}>
            {showPast ? "지난 일정 숨기기" : `지난 일정 보기 (${past.length})`}
          </button>
          {showPast && (
            <div className="sstoa-panel" style={{ marginTop: 10 }}>
              <ScheduleTable rows={past} today={today} editingId={editingId} onEdit={startEdit} onRemove={remove} onResync={resync} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScheduleTable({ rows, today, editingId, onEdit, onRemove, onResync }) {
  return (
    <table className="sstoa-table">
      <thead><tr><th>날짜</th><th>시간</th><th>할일</th><th>관련 프로젝트</th><th>캘린더</th><th></th></tr></thead>
      <tbody>
        {rows.map((s) => (
          <tr key={s.id} style={{ cursor: "default", background: editingId === s.id ? "var(--s-accent-weak)" : undefined }}>
            <td style={{ whiteSpace: "nowrap", fontWeight: s.date === today ? 700 : 400 }}>
              {withWeekday(s.date)}{s.date === today ? " · 오늘" : ""}
            </td>
            <td style={{ whiteSpace: "nowrap" }}>{s.time || "종일"}</td>
            <td style={{ fontWeight: 600 }}>{s.title}</td>
            <td>{s.project ? <Link to={`/sstoa/projects/${s.project.id}`}>{s.project.name}</Link> : "—"}</td>
            <td style={{ whiteSpace: "nowrap" }}>
              {s.gcal_event_id && !s.gcal_error ? (
                <span className="sstoa-tag">등록됨</span>
              ) : (
                <>
                  <span className="sstoa-tag sstoa-tag--gray" title={s.gcal_error || ""} style={{ color: s.gcal_error ? "var(--s-danger)" : undefined }}>
                    {s.gcal_error ? "실패" : "미등록"}
                  </span>{" "}
                  <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => onResync(s)} title={s.gcal_error || ""}>다시 보내기</button>
                </>
              )}
            </td>
            <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
              <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => onEdit(s)}>수정</button>{" "}
              <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => onRemove(s)}>삭제</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
